import QcAuthorAvatar from './QcAuthorAvatar';
import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/Close';
import { QcComment } from '../../../interfaces/qcIssue.interface';
import { formatQcDateTime, ACCENT_BLUE, ACCENT_BLUE_DARK } from './qcTheme';
import { CloseButton, LinkButton, ModalHeader, ModalLede } from './workspaceModalParts';
import {
  applyMention,
  getMentionMatches,
  getMentionTerm,
  OrderedQuestionEntry,
} from './workspaceDerivations';
import QuestionMentionMenu from './QuestionMentionMenu';

const Title = styled('h2')({
  fontSize: '1.25rem',
  fontWeight: 500,
  color: 'rgba(0,0,0,0.87)',
  margin: 0,
});

// Gen-thread avatars: admin blue, everyone else neutral gray (prototype genComments — not the reply teal).
const Body = styled('div')({
  flex: 1,
  overflow: 'auto',
  padding: '20px 24px 4px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

const CommentRow = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
});

const CommentMeta = styled('div')({
  display: 'flex',
  alignItems: 'baseline',
  gap: '8px',
  flexWrap: 'wrap',
});

const CommentAuthor = styled('span')({
  fontSize: '0.85rem',
  fontWeight: 700,
  color: '#424242',
});

const CommentSubtle = styled('span')({
  fontSize: '0.72rem',
  color: 'rgba(0,0,0,0.5)',
});

const CommentText = styled('p')({
  fontSize: '0.9rem',
  color: 'rgba(0,0,0,0.87)',
  lineHeight: 1.55,
  margin: '4px 0 0',
});

const Draft = styled('textarea')({
  width: '100%',
  boxSizing: 'border-box',
  minHeight: '90px',
  border: '1px solid rgba(0,0,0,0.3)',
  borderRadius: '6px',
  padding: '10px 12px',
  fontFamily: 'inherit',
  fontSize: '0.9rem',
  color: 'rgba(0,0,0,0.87)',
  resize: 'vertical',
  outline: 'none',
  background: '#fff',
  '&:focus': {
    borderColor: ACCENT_BLUE,
    boxShadow: '0 0 0 2px rgba(25,118,210,0.18)',
  },
});

const Footer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '10px',
  padding: '18px 24px 22px',
  borderTop: '1px solid rgba(0,0,0,0.1)',
});

const PostButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: ACCENT_BLUE,
  color: '#fff',
  border: 'none',
  borderRadius: '4px',
  padding: '8px 16px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.46px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': {
    background: ACCENT_BLUE_DARK,
  },
  '&:disabled': {
    background: 'rgba(0,0,0,0.1)',
    color: 'rgba(0,0,0,0.32)',
    cursor: 'default',
  },
});

export interface QcCommentsModalProps {
  open: boolean;
  questionCode: string;
  comments: QcComment[];
  entries?: OrderedQuestionEntry[];
  onPost: (text: string) => void;
  onClose: () => void;
}

export default function QcCommentsModal({
  open, questionCode, comments, entries = [], onPost, onClose,
}: QcCommentsModalProps): JSX.Element {
  const [draft, setDraft] = useState('');
  const [mentionDismissed, setMentionDismissed] = useState(false);
  const mentionTerm = mentionDismissed ? null : getMentionTerm(draft);
  const mentionMatches = mentionTerm === null ? [] : getMentionMatches(entries, mentionTerm, 5);

  const handleClose = () => {
    setDraft('');
    onClose();
  };

  const handlePost = () => {
    if (!draft.trim()) return;
    onPost(draft.trim());
    setDraft('');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-label='Comments'
      PaperProps={{ sx: { width: 680, maxWidth: '100%', maxHeight: 'calc(100vh - 64px)', borderRadius: '4px' } }}
      maxWidth={false}
    >
      <ModalHeader>
        <div>
          <Title>Comments ({comments.length})</Title>
          <ModalLede>
            General thread between the assessor and TPN Admin on {questionCode}. Not tied to a QC
            issue and never a status change.
          </ModalLede>
        </div>
        <CloseButton type='button' aria-label='Close comments' onClick={handleClose}>
          <CloseIcon sx={{ fontSize: 22 }} />
        </CloseButton>
      </ModalHeader>
      <Body>
        {comments.map(comment => (
          <CommentRow key={comment.id}>
            <QcAuthorAvatar author={comment.author} size={32} />
            <div>
              <CommentMeta>
                <CommentAuthor>{comment.author.name}</CommentAuthor>
                <CommentSubtle>{comment.author.role === 'tpn_admin' ? 'TPN Admin' : 'Assessor'}</CommentSubtle>
                <CommentSubtle>{formatQcDateTime(comment.createdAt)}</CommentSubtle>
              </CommentMeta>
              <CommentText>{comment.text}</CommentText>
            </div>
          </CommentRow>
        ))}
        <Draft
          aria-label='Add a comment'
          placeholder='Add a comment… Type # to link a question.'
          value={draft}
          onChange={event => { setDraft(event.target.value); setMentionDismissed(false); }}
          onKeyDown={event => { if (event.key === 'Escape') setMentionDismissed(true); }}
        />
        <QuestionMentionMenu
          matches={mentionMatches}
          onPick={shortCode => { setDraft(prev => applyMention(prev, shortCode)); setMentionDismissed(false); }}
        />
      </Body>
      <Footer>
        <LinkButton type='button' onClick={handleClose}>Close</LinkButton>
        <PostButton type='button' disabled={!draft.trim()} onClick={handlePost}>Comment</PostButton>
      </Footer>
    </Dialog>
  );
}
